<template>
    <v-card flat color="transparent" class="w-100 align-self-start">
        <v-card-title>
            <span class="secondary--text">Instances</span>
        </v-card-title>
        <v-card-text>
            <div v-if="!fetchingInstances" class="d-flex flex-wrap align-center">
                <add-instance-dialog v-if="spaceInstances.length">
                    <v-btn color="primary">
                        <v-icon small left>mdi-plus</v-icon>
                        <span>Instance</span>
                    </v-btn>
                </add-instance-dialog>
                <v-spacer />
                <v-text-field
                    class="mx-2"
                    v-show="spaceInstances.length"
                    placeholder="Search for an instance..."
                    autofocus
                    prepend-inner-icon="mdi-magnify "
                    v-model="instanceSearch"
                    solo
                    flat
                    hide-details
                    dense
                    clearable>
                    <template v-slot:append>
                        <v-menu offset-y :close-on-content-click="false">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                    <v-icon left>mdi-tune-variant</v-icon>
                                    <span>Filters</span>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <div v-for="(item, index) in Object.entries(userCheckboxBinders)" :key="index">
                                    <v-sheet rounded class="py-2 px-4 my-1">
                                        <v-switch inset dense hide-details class="pa-0 ma-0" v-model="item[1].status">
                                            <template v-slot:label>
                                                <div class="d-flex align-center">
                                                    <v-icon left small>{{ item[1].icon }}</v-icon>
                                                    <span class="text-capitalize">{{ item[0] }}</span>
                                                </div>
                                            </template>
                                        </v-switch>
                                    </v-sheet>
                                </div>
                                <v-divider class="my-2" />
                                <v-sheet rounded class="py-2 px-4 my-1">
                                    <v-switch v-model="showInheritedRoles" inset dense hide-details class="pa-0 ma-0">
                                        <template v-slot:label>
                                            <div class="d-flex align-center">
                                                <v-icon left small>mdi-chevron-double-down</v-icon>
                                                <span>Inherited members</span>
                                            </div>
                                        </template>
                                    </v-switch>
                                </v-sheet>
                            </v-list>
                        </v-menu>
                    </template>
                </v-text-field>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" @click="$store.dispatch('spaceStore/fetchSpaceInstances', $route.params.sid)" text icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>refresh</span>
                </v-tooltip>
            </div>

            <div v-if="!fetchingInstances">
                <v-data-table
                    v-if="spaceInstances.length"
                    :headers="instanceHeaders"
                    :items="filteredInstancesByRole"
                    item-key="iid"
                    :items-per-page.sync="itemsPerPage"
                    :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                    :expanded.sync="expanded"
                    show-expand
                    class="mt-4">
                    <template v-slot:top="{ options, pagination, updateOptions }">
                        <div class="d-flex align-center">
                            <v-spacer></v-spacer>
                            <v-data-footer
                                :pagination="pagination"
                                :options="options"
                                @update:options="updateOptions"
                                :itemsPerPageOptions="itemsPerPageOptions"
                                items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                                class="no-border" />
                        </div>
                    </template>
                    <template v-slot:[`item.type`]="{ item }">
                        <v-icon>{{ getInstanceTypeIcon(item) }}</v-icon>
                    </template>
                    <template v-slot:[`item.instance`]="{ item }">
                        <v-card flat color="transparent" class="my-1 d-inline-flex">
                            <v-list-item
                                two-line
                                class="rounded overflow-hidden"
                                :to="{
                                    name: 'snapshot-overview',
                                    params: { oid: $route.params.oid, sid: $route.params.sid, iid: item.iid, snid: item.dev_snid }
                                }">
                                <v-list-item-content class="d-inline-block">
                                    <v-list-item-title>
                                        <b>{{ item.long_id }}</b>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-icon right color="primary" class="showOnHover">mdi-chevron-right</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                        </v-card>
                    </template>
                    <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
                        <v-btn v-if="usersPerInstance(item).length" icon small @click.stop="expand(!isExpanded)">
                            <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </v-btn>
                    </template>
                    <template v-slot:expanded-item="{ headers, item }">
                        <td :colspan="headers.length" class="pa-0">
                            <div class="grey lighten-4 pl-15">
                                <v-row no-gutters>
                                    <v-col cols="6" class="px-4 py-3"><b>User</b></v-col>
                                    <v-col cols="4" class="px-4 py-3"><b>Role</b></v-col>
                                    <v-col cols="2" class="px-4 py-3 text-right"><b>Actions</b></v-col>
                                </v-row>
                                <v-divider />
                                <div v-for="user in usersPerInstance(item)" :key="`${item.iid}-${user.ucid}-${user.role_name}`">
                                    <v-row no-gutters align="center">
                                        <v-col cols="6" class="d-flex align-center px-4 py-2">
                                            <v-tooltip v-if="user.inheritedFrom" bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon v-bind="attrs" v-on="on" small color="secondary" class="mr-2">
                                                        {{ getInheritedRoleIcon(user.inheritedFrom) }}
                                                    </v-icon>
                                                </template>
                                                <span>Inherited from {{ user.inheritedFrom }}</span>
                                            </v-tooltip>
                                            <span>{{ user.full_name || user.email || '-' }}</span>
                                        </v-col>
                                        <v-col cols="4" class="px-4 py-2">
                                            <v-chip outlined label small>
                                                <v-icon left small>{{ getUserRoleIcon(user) }}</v-icon>
                                                {{ getUserRoleLabel(user) }}
                                            </v-chip>
                                        </v-col>
                                        <v-col cols="2" class="px-4 py-2 text-right">
                                            <v-menu v-if="!user.inheritedFrom" left offset-x nudge-top="10">
                                                <template v-slot:activator="{ on }">
                                                    <v-btn v-on="on" text color="secondary" small>
                                                        <v-icon>more_horiz</v-icon>
                                                    </v-btn>
                                                </template>
                                                <v-list nav dense>
                                                    <InstanceUserRoleDialog
                                                        :ucid="user.ucid.toString()"
                                                        :userName="user.full_name"
                                                        :email="user.email"
                                                        :instanceId="item.iid.toString()"
                                                        :instanceName="item.long_id"
                                                        :role="user.role_name"
                                                        @role-updated="fetchUsers" />
                                                    <v-divider class="my-2" />
                                                    <UserActions
                                                        :ucid="user.ucid.toString()"
                                                        :userName="user.full_name"
                                                        :hierarchicalLevel="nuvolosObjectTypes.INSTANCE"
                                                        :instanceId="item.iid.toString()"
                                                        :instanceName="item.long_id"
                                                        :role="user.role_name"
                                                        reason="explicit"
                                                        :spaceAdmin="false"
                                                        activatorType="listItem"
                                                        @close-menu="fetchUsers" />
                                                </v-list>
                                            </v-menu>
                                        </v-col>
                                    </v-row>
                                    <v-divider />
                                </div>
                            </div>
                        </td>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu left offset-x nudge-top="10">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" text color="secondary" small>
                                    <v-icon>more_horiz</v-icon>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <instance-edit-dialog :instance="item">
                                    <template v-slot:activator="{ attrs, disabled, open }">
                                        <v-list-item v-bind="attrs" :disabled="disabled" @click.stop="open">
                                            <v-list-item-icon>
                                                <v-icon :color="disabled ? null : 'secondary'" :class="disabled ? 'text--disabled' : ''">
                                                    mdi-pencil-outline
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title :class="[disabled ? 'text--disabled' : 'secondary--text', 'mr-5']">Edit</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </instance-edit-dialog>
                                <instance-invite-dialog :instanceName="item.long_id">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-list-item v-bind="attrs" v-on="on">
                                            <v-list-item-icon>
                                                <v-icon color="secondary">mdi-account-plus-outline</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="secondary--text mr-5">Invite users</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </instance-invite-dialog>
                                <v-divider class="my-2"></v-divider>
                                <instance-delete-dialog
                                    :instanceId="parseInt(item.iid, 10)"
                                    :instanceName="item.long_id"
                                    :masterInstanceId="masterInstance.iid.toString()"
                                    :masterDevelopmentSnapshotId="masterInstance.dev_snid.toString()">
                                    <template v-slot:activator="{ attrs, disabled, open }">
                                        <v-list-item v-bind="attrs" :disabled="disabled" @click.stop="open">
                                            <v-list-item-icon>
                                                <v-icon :color="disabled ? null : 'error'" :class="disabled ? 'text--disabled' : ''">
                                                    mdi-trash-can-outline
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title :class="[disabled ? 'text--disabled' : 'error--text', 'mr-5']">Delete</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </instance-delete-dialog>
                            </v-list>
                        </v-menu>
                    </template>
                    <template v-slot:no-data>
                        <span v-if="instanceSearch">No instances found.</span>
                        <span v-else>No instances found. Consider adding a new instance above.</span>
                    </template>
                </v-data-table>

                <div v-else>
                    <v-banner class="mb-5" two-line>
                        <v-avatar slot="icon" color="white" size="60"><v-icon x-large color="info">mdi-information-outline</v-icon></v-avatar>
                        <div class="d-flex flex-column">
                            <span class="secondary--text font-weight-bold mb-1">No instances found</span>
                        </div>
                        <template v-slot:actions>
                            <add-instance-dialog>
                                <v-btn color="primary">
                                    <v-icon small>mdi-plus</v-icon>
                                    <span>Instance</span>
                                </v-btn>
                            </add-instance-dialog>
                        </template>
                    </v-banner>
                </div>
            </div>

            <div v-else>
                <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { getInstanceTypeIcon, instanceRole } from '@/utils'
const InstanceDeleteDialog = () => import('@/modules/instance/components/TheInstanceDeleteDialog.vue')
const InstanceEditDialog = () => import('@/modules/instance/components/InstanceEditDialog.vue')
const InstanceInviteDialog = () => import('@/modules/instance/components/InstanceInviteDialog.vue')
const InstanceUserRoleDialog = () => import('@/modules/instance/components/InstanceUserRoleDialog.vue')
const AddInstanceDialog = () => import('@/modules/space/components/TheSpaceAddInstanceDialog.vue')
const UserActions = () => import('@/components/UserActions.vue')

export default {
    name: 'InstanceList',
    mixins: [enumsData],
    components: { InstanceDeleteDialog, AddInstanceDialog, InstanceEditDialog, InstanceInviteDialog, InstanceUserRoleDialog, UserActions },
    data() {
        return {
            instanceSearch: '',
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            expanded: [],
            showInheritedRoles: false,
            userCheckboxBinders: {
                administrators: { status: true, icon: 'mdi-shield-account-outline' },
                editors: { status: true, icon: instanceRole('editor').icon },
                viewers: { status: true, icon: instanceRole('viewer').icon },
                observers: { status: true, icon: instanceRole('observer').icon }
            }
        }
    },
    methods: {
        instanceRolesPerInstance: function (instanceName) {
            return this.instanceRoles.filter(instance => instance.Instance === instanceName)
        },
        userGroupsPerInstance(instance) {
            const directUsers = []
            const spaceInheritedUsers = []
            const organizationInheritedUsers = []
            const addedUserIds = new Set()
            const addUser = (users, user) => {
                const userId = user.uid || user.ucid
                if (!addedUserIds.has(userId)) {
                    users.push(user)
                    addedUserIds.add(userId)
                }
            }

            this.instanceRolesPerInstance(instance.long_id).forEach(user => addUser(directUsers, user))

            if (this.showInheritedRoles) {
                if (this.userCheckboxBinders.administrators.status) {
                    this.spaceAdmins.forEach(user =>
                        addUser(spaceInheritedUsers, { ...user, role_name: this.roleTypes.SPACE_ADMIN, inheritedFrom: 'space administrator' })
                    )
                }

                if (this.userCheckboxBinders.viewers.status) {
                    this.organizationManagers.forEach(user =>
                        addUser(organizationInheritedUsers, {
                            ...user,
                            role_name: this.roleTypes.INSTANCE_VIEWER,
                            inheritedFrom: 'organization manager'
                        })
                    )
                }
            }

            const sortUsers = users =>
                users.sort((firstUser, secondUser) =>
                    (firstUser.full_name || firstUser.email || '').localeCompare(secondUser.full_name || secondUser.email || '', undefined, {
                        sensitivity: 'base'
                    })
                )

            return [
                { key: 'direct', users: sortUsers(directUsers) },
                { key: 'space', users: sortUsers(spaceInheritedUsers) },
                { key: 'organization', users: sortUsers(organizationInheritedUsers) }
            ].filter(group => group.users.length)
        },
        usersPerInstance(instance) {
            return this.userGroupsPerInstance(instance).reduce((users, group) => users.concat(group.users), [])
        },
        getInheritedRoleIcon(inheritedFrom) {
            return inheritedFrom === 'space administrator' ? 'mdi-chevron-double-down' : 'mdi-chevron-triple-down'
        },
        getUserRoleIcon(user) {
            return user.role_name === this.roleTypes.SPACE_ADMIN ? 'mdi-shield-account-outline' : this.getRoleIcon(user.role_name)
        },
        getUserRoleLabel(user) {
            return user.role_name === this.roleTypes.SPACE_ADMIN ? 'Administrator' : instanceRole(user.role_name).label
        },
        getRoleIcon(roleName) {
            return instanceRole(roleName).icon
        },
        getInstanceTypeIcon(instance) {
            return getInstanceTypeIcon(instance?.long_id)
        },
        fetchUsers() {
            return this.$store.dispatch('spaceStore/fetchSpaceUsers', this.$route.params.sid)
        }
    },
    mounted() {
        this.fetchUsers()
    },
    computed: {
        ...mapState('spaceStore', ['spaceInstances', 'spaceUsers', 'fetchingInstances']),
        ...mapGetters('spaceStore', ['currentSpaceType']),
        instanceHeaders() {
            return [
                { text: '', value: 'data-table-expand', sortable: false, width: '48px' },
                { text: '', value: 'type', sortable: false, width: '48px' },
                { text: 'Instance', value: 'instance', sortable: false },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '120px' }
            ]
        },
        instanceRoles() {
            if (this.spaceUsers) {
                return this.spaceUsers
                    .filter(user => user.instance_viewer_roles.length || user.instance_editor_roles.length || user.instance_observer_roles.length)
                    .map(user => {
                        let finalData = []
                        if (this.userCheckboxBinders.editors.status) {
                            const userEditorData = user.instance_editor_roles.map(instance =>
                                Object.assign({}, instance, {
                                    ucid: user.ucid,
                                    uid: user.uid,
                                    full_name: user.full_name,
                                    email: user.email,
                                    is_active: user.is_active
                                })
                            )
                            finalData = finalData.concat(userEditorData)
                        }
                        if (this.userCheckboxBinders.viewers.status) {
                            const userViewerData = user.instance_viewer_roles.map(instance =>
                                Object.assign({}, instance, {
                                    ucid: user.ucid,
                                    uid: user.uid,
                                    full_name: user.full_name,
                                    email: user.email,
                                    is_active: user.is_active
                                })
                            )
                            finalData = finalData.concat(userViewerData)
                        }
                        if (this.userCheckboxBinders.observers.status) {
                            const userObserverData = user.instance_observer_roles.map(instance =>
                                Object.assign({}, instance, {
                                    ucid: user.ucid,
                                    uid: user.uid,
                                    full_name: user.full_name,
                                    email: user.email,
                                    is_active: user.is_active
                                })
                            )
                            finalData = finalData.concat(userObserverData)
                        }
                        return finalData
                    })
                    .reduce((a, b) => [...a, ...b], [])
            }
            return []
        },
        filteredSpaceInstances() {
            if (!this.instanceSearch) return this.spaceInstances
            const keyword = this.instanceSearch.toLowerCase()
            return this.spaceInstances.filter(instance => {
                const name = (instance.long_id || '').toLowerCase()
                const description = (instance.description || '').toLowerCase()
                return name.includes(keyword) || description.includes(keyword)
            })
        },
        filteredInstancesByRole() {
            const allRolesSelected = Object.values(this.userCheckboxBinders).every(filter => filter.status)
            if (allRolesSelected) {
                return this.filteredSpaceInstances
            }

            return this.filteredSpaceInstances.filter(instance => {
                const roles = this.instanceRolesPerInstance(instance.long_id)
                const hasAdmin = this.showInheritedRoles && this.spaceAdmins.length > 0
                const hasEditor = roles.some(role => role.role_name === this.roleTypes.INSTANCE_EDITOR)
                const hasInheritedViewer = this.showInheritedRoles && this.organizationManagers.length > 0
                const hasExplicitViewer = roles.some(role => role.role_name === this.roleTypes.INSTANCE_VIEWER)
                const hasViewer = hasInheritedViewer || hasExplicitViewer
                const hasObserver = roles.some(role => role.role_name === this.roleTypes.INSTANCE_OBSERVER)

                return (
                    (this.userCheckboxBinders.administrators.status && hasAdmin) ||
                    (this.userCheckboxBinders.editors.status && hasEditor) ||
                    (this.userCheckboxBinders.viewers.status && hasViewer) ||
                    (this.userCheckboxBinders.observers.status && hasObserver)
                )
            })
        },
        spaceAdmins() {
            return this.spaceUsers.filter(user => (user.role || user.space_role) === this.roleTypes.SPACE_ADMIN)
        },
        organizationManagers() {
            return this.spaceUsers.filter(user => user.reason === 'organization_manager')
        },
        masterInstance() {
            const masterInstance = this.spaceInstances.find(instance => instance.long_id === this.instanceFixedNames.MASTER_INSTANCE)
            return masterInstance
        }
    }
}
</script>

<style scoped lang="scss">
.activeClass::before {
    opacity: 0;
}
</style>
